import {z} from 'zod'

export const ActivityReportSector = z.enum(['outpatient', 'domiciliary'])
export type ActivityReportSector = z.infer<typeof ActivityReportSector>

export const ActivityReportStatus = z.enum(['pending', 'billed'])
export type ActivityReportStatus = z.infer<typeof ActivityReportStatus>

// Embedded export job. Modeled tolerantly (status as a plain string, optional fields) so an
// abbreviated or extended backend payload never makes the whole history response fail to parse.
// The shared strict ExportJob schema (@/features/exports/types) requires entity/format/requestedAt,
// which the embedded object may omit. `.catch(null)` on the field (see ActivityReport) further
// degrades an unparseable job to null rather than dropping the row.
export const ActivityReportExportJob = z.object({
    id: z.string(),
    status: z.string(),
    downloadUrl: z.string().nullish(),
    fileName: z.string().nullish(),
    format: z.string().nullish()
})
export type ActivityReportExportJob = z.infer<typeof ActivityReportExportJob>

export const ActivityReport = z.object({
    id: z.string(),
    sector: ActivityReportSector,
    startDate: z.string(),
    endDate: z.string(),
    status: ActivityReportStatus,
    accessesCount: z.number(),
    absencesCount: z.number(),
    billedAt: z.string().nullable(),
    createdAt: z.string(),
    // Present on the create/regenerate 202 response; absent on history rows.
    exportJobId: z.string().nullish(),
    exportJob: ActivityReportExportJob.nullable().catch(null)
})
export type ActivityReport = z.infer<typeof ActivityReport>

export const ExistingActivityReportRef = z.object({
    id: z.string(),
    sector: ActivityReportSector,
    startDate: z.string(),
    endDate: z.string(),
    createdAt: z.string(),
    // Tolerant so an older backend payload that omits the flag keeps generation enabled.
    isBilled: z.boolean().catch(false)
})
export type ExistingActivityReportRef = z.infer<typeof ExistingActivityReportRef>

export const ActivityReportCounters = z.object({
    validatedAccesses: z.number(),
    totalAccesses: z.number(),
    validatedAbsences: z.number(),
    totalAbsences: z.number(),
    existingReport: ExistingActivityReportRef.nullable()
})
export type ActivityReportCounters = z.infer<typeof ActivityReportCounters>

export const ActivityReportError = z.object({
    code: z.string(),
    message: z.string()
})
export type ActivityReportError = z.infer<typeof ActivityReportError>

export const ActivityReportsColumn = z.enum([
    'select',
    'period',
    'sector',
    'accesses',
    'absences',
    'status',
    'file',
    'createdAtAndAuthor',
    'moreActions'
])
export type ActivityReportsColumn = z.infer<typeof ActivityReportsColumn>

export const ActivityReportHistorySearchParams = z.object({
    sector: ActivityReportSector.optional(),
    status: ActivityReportStatus.optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().default(20)
})
export type ActivityReportHistorySearchParams = z.infer<typeof ActivityReportHistorySearchParams>

/* --- DSP Flux (PRI txt) export (feature 010) --- */

// GET /accesses/pri-txt/available-months response. `months` are 'MM-YYYY' strings, server-sorted
// most-recent-first, and contain only periods whose export can be generated. Empty ⇒ nothing exportable.
export const AvailablePriTxtMonths = z.object({
    months: z.array(z.string())
})
export type AvailablePriTxtMonths = z.infer<typeof AvailablePriTxtMonths>

// POST /accesses/export-pri-txt-by-month body.
export const PriTxtExportRequest = z.object({
    month: z.number().int().min(1).max(12),
    year: z.number().int().min(2000).max(2100)
})
export type PriTxtExportRequest = z.infer<typeof PriTxtExportRequest>

// 500 error body (delivered as a blob, converted to JSON by the axios interceptor).
export const PriTxtExportError = z.object({
    code: z.literal('pri-export-failed'),
    message: z.string().optional()
})
export type PriTxtExportError = z.infer<typeof PriTxtExportError>
